import axios from 'axios';
import { builderSurfaceJsonContentHeader } from '../../../common/utils/restApiHeaderUtils';

const getBaseUrl = ({ version }) =>
  `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/vibe-coding`;

const getConversationUrl = (args, suffix = '') =>
  `${getBaseUrl(args)}/conversations/${args.conversationId}${suffix}`;

const getPreviewEnsureUrl = (args) => {
  return workspaceUrl(args, 'preview');
};
const workspaceUrl = (args, suffix) => `${getBaseUrl(args)}/workspace/${suffix}`;

export const fetchConversations = async (args = {}) => {
  const response = await axios.get(
    `${getBaseUrl(args)}/conversations`,
    builderSurfaceJsonContentHeader(args.projectId),
  );
  return response.data;
};

export const fetchProjectUsage = async ({ version, projectId } = {}) => {
  if (!version || !projectId) {
    throw new Error('version and projectId are required');
  }
  const response = await axios.get(
    `${getBaseUrl({ version })}/usage`,
    builderSurfaceJsonContentHeader(projectId, true),
  );
  return response.data;
};
export const fetchDefaultConversation = async (args = {}) => {
  const response = await axios.get(
    `${getBaseUrl(args)}/conversations/default`,
    builderSurfaceJsonContentHeader(args.projectId),
  );
  return response.data;
};

export const generateVibeResponse = async (args = {}) => {
  const response = await axios.post(
    `${getBaseUrl(args)}/generate`,
    args.data,
    builderSurfaceJsonContentHeader(args.projectId),
  );
  return response.data;
};

export const fetchConversationHistory = async (args = {}) => {
  const params = {};
  if (args.limit) params.limit = args.limit;
  if (args.before) params.before = args.before;
  const response = await axios.get(getConversationUrl(args, '/history'), {
    ...builderSurfaceJsonContentHeader(args.projectId),
    params,
  });
  return response.data;
};

export const clearConversation = async (args = {}) => {
  if (!args.conversationId) return null;
  const response = await axios.delete(
    getConversationUrl(args, '/history'),
    builderSurfaceJsonContentHeader(args.projectId),
  );
  return response.data;
};

export const fetchWorkspace = async (args = {}) => {
  const response = await axios.get(
    getConversationUrl(args, '/workspace'),
    builderSurfaceJsonContentHeader(args.projectId),
  );
  return response.data;
};

export const fetchWorkspaceFile = async (args = {}) => {
  const response = await axios.get(getConversationUrl(args, '/files'), {
    ...builderSurfaceJsonContentHeader(args.projectId),
    params: { path: args.path },
  });
  return response.data;
};

export const fetchEnsureWorkspacePreview = async (args = {}) => {
  const response = await axios.post(
    getPreviewEnsureUrl(args),
    {
      rebuild: Boolean(args.rebuild),
      reinstall: Boolean(args.reinstall),
    },
    {
      ...builderSurfaceJsonContentHeader(args.projectId),
      params: {
        projectId: args.projectId,
        version: args.version,
        seoName: args.seoName,
      },
    },
  );
  return response.data;
};
export const updateWorkspaceFile = async (args = {}) => {
  const response = await axios.put(
    getConversationUrl(args, '/files'),
    { content: args.content, baseHash: args.baseHash },
    { ...builderSurfaceJsonContentHeader(args.projectId), params: { path: args.path } },
  );
  return response.data;
};

export const startVibeGenerationStream = (args = {}, signal) => {
  return fetch(`${getBaseUrl(args)}/generate/stream`, {
    method: 'POST',
    headers: builderSurfaceJsonContentHeader(args.projectId, true).headers,
    body: JSON.stringify(args.data),
    signal,
  });
};

export const processFileCreate = async (args = {}) => {
  const { projectId, path, folderPath, content = '' } = args;
  const relative = String(path || folderPath || '')
    .trim()
    .replace(/^\/+/, '');
  if (!projectId) throw new Error('projectId and version are required');
  if (!path || !String(path).trim()) throw new Error('path is required');

  const response = await axios.post(
    workspaceUrl(args, 'files/create'),
    {
      folderPath: relative,
      path: relative,
      content,
    },
    {
      ...builderSurfaceJsonContentHeader(projectId),
      timeout: 60000,
    },
  );

  return response.data;
};
export const processFolderCreate = async (args = {}) => {
  const { projectId, path, folderPath } = args;
  const relative = String(path || folderPath || '')
    .trim()
    .replace(/^\/+/, '');

  if (!projectId) throw new Error('projectId is required');
  if (!relative) throw new Error('path is required');

  const response = await axios.post(
    workspaceUrl(args, 'folders/create'),
    { folderPath: relative, path: relative },
    {
      ...builderSurfaceJsonContentHeader(projectId),
      timeout: 60000,
    },
  );
  return response.data;
};

export const processFileChange = async (args = {}) => {
  const { projectId, from, to } = args;
  if (!projectId) throw new Error('projectId is required');
  if (!from || !to) throw new Error('from and to are required');

  const response = await axios.post(
    `${getBaseUrl(args)}/files/rename`,
    {
      from: String(from).trim().replace(/^\/+/, ''),
      to: String(to).trim().replace(/^\/+/, ''),
    },
    {
      ...builderSurfaceJsonContentHeader(projectId),
      timeout: 60000,
    },
  );
  return response.data;
};
export const processFileDelete = async (args = {}) => {
  const { projectId, path, type = 'file' } = args;
  const relative = String(path || '')
    .trim()
    .replace(/^\/+/, '');

  if (!projectId) throw new Error('projectId is required');
  if (!relative) throw new Error('path is required');

  const response = await axios.post(
    `${getBaseUrl(args)}/files/delete`,
    { path: relative, type },
    {
      ...builderSurfaceJsonContentHeader(projectId),
      timeout: 60000,
    },
  );
  return response.data;
};
